/**
 * Career schemas
 *
 * Validation schemas for Career feature - opportunities and CV builder.
 */

import { z } from 'zod';

// ═══════════════════════════════════════════════════════════════════════
// OPPORTUNITY SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Employment type enum
 */
export const employmentTypeSchema = z.enum([
  'full_time',
  'part_time',
  'contract',
  'freelance',
  'internship',
]);

/**
 * Experience level enum
 */
export const experienceLevelSchema = z.enum([
  'entry',
  'junior',
  'mid',
  'senior',
  'lead',
  'executive',
]);

/**
 * Search opportunities schema
 */
export const searchOpportunitiesSchema = z.object({
  query: z.string().max(200).optional(),
  location: z.string().max(100).optional(),
  country: z.string().length(2).optional(), // ISO country code
  employmentTypes: z.array(employmentTypeSchema).optional(),
  experienceLevels: z.array(experienceLevelSchema).optional(),
  industries: z.array(z.string()).optional(),
  skills: z.array(z.string()).optional(),
  salaryMin: z.number().int().min(0).optional(),
  salaryMax: z.number().int().min(0).optional(),
  currency: z.string().length(3).default('EUR'), // ISO currency code
  remote: z.boolean().optional(),
  sponsorship: z.boolean().optional(), // Visa sponsorship available
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(50).default(20),
  sortBy: z.enum(['relevance', 'date', 'salary']).default('relevance'),
});

/**
 * View opportunity schema
 */
export const viewOpportunitySchema = z.object({
  opportunityId: z.uuid(),
});

/**
 * Save opportunity schema
 */
export const saveOpportunitySchema = z.object({
  opportunityId: z.uuid(),
  notes: z.string().max(1000).optional(),
});

/**
 * Express interest schema
 */
export const expressInterestSchema = z.object({
  opportunityId: z.uuid(),
  coverLetter: z.string().max(5000).optional(),
  cvId: z.uuid().optional(), // Use specific CV version
  availableFrom: z.coerce.date().optional(),
  salaryExpectation: z.number().int().min(0).optional(),
  notes: z.string().max(2000).optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// CV BUILDER SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * CV section schema
 */
export const cvSectionSchema = z.enum([
  'personal',
  'summary',
  'experience',
  'education',
  'skills',
  'languages',
  'certifications',
  'projects',
  'references',
]);

/**
 * CV personal info schema
 */
export const cvPersonalInfoSchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  email: z.email(),
  phone: z.string().max(20).optional(),
  location: z.string().max(200).optional(),
  website: z.url().optional(),
  linkedinUrl: z.url().optional(),
  portfolioUrl: z.url().optional(),
});

/**
 * CV experience entry schema
 */
export const cvExperienceSchema = z.object({
  id: z.uuid().optional(),
  company: z.string().min(1).max(200),
  title: z.string().min(1).max(200),
  location: z.string().max(200).optional(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date().optional(),
  current: z.boolean().default(false),
  description: z.string().max(3000).optional(),
  highlights: z.array(z.string().max(500)).max(10).optional(),
});

/**
 * CV education entry schema
 */
export const cvEducationSchema = z.object({
  id: z.uuid().optional(),
  institution: z.string().min(1).max(200),
  degree: z.string().min(1).max(200),
  field: z.string().max(200).optional(),
  location: z.string().max(200).optional(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date().optional(),
  gpa: z.string().max(20).optional(),
  honors: z.string().max(500).optional(),
});

/**
 * CV skill entry schema
 */
export const cvSkillSchema = z.object({
  name: z.string().min(1).max(100),
  level: z.enum(['beginner', 'intermediate', 'advanced', 'expert']).optional(),
  yearsOfExperience: z.number().int().min(0).max(50).optional(),
});

/**
 * CV language entry schema
 */
export const cvLanguageSchema = z.object({
  name: z.string().min(1).max(100),
  level: z.enum(['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'native']),
  certification: z.string().max(200).optional(),
});

/**
 * Generate CV schema
 */
export const generateCVSchema = z.object({
  journeyInstanceId: z.uuid(),
  template: z.enum(['modern', 'classic', 'minimal', 'creative']).default('modern'),
  personalInfo: cvPersonalInfoSchema,
  summary: z.string().max(2000).optional(),
  experience: z.array(cvExperienceSchema).max(20).optional(),
  education: z.array(cvEducationSchema).max(10).optional(),
  skills: z.array(cvSkillSchema).max(50).optional(),
  languages: z.array(cvLanguageSchema).max(10).optional(),
  sectionsOrder: z.array(cvSectionSchema).optional(),
  aiEnhance: z.boolean().default(false), // Use AI to enhance content
});

/**
 * Download CV schema
 */
export const downloadCVSchema = z.object({
  cvId: z.uuid(),
  format: z.enum(['pdf', 'docx']),
});

/**
 * Update CV schema
 */
export const updateCVSchema = z.object({
  cvId: z.uuid(),
  updates: generateCVSchema.partial().omit({ journeyInstanceId: true }),
});

// ═══════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════

export type EmploymentType = z.infer<typeof employmentTypeSchema>;
export type ExperienceLevel = z.infer<typeof experienceLevelSchema>;
export type SearchOpportunitiesInput = z.infer<typeof searchOpportunitiesSchema>;
export type ViewOpportunityInput = z.infer<typeof viewOpportunitySchema>;
export type SaveOpportunityInput = z.infer<typeof saveOpportunitySchema>;
export type ExpressInterestInput = z.infer<typeof expressInterestSchema>;
export type CVSection = z.infer<typeof cvSectionSchema>;
export type CVPersonalInfo = z.infer<typeof cvPersonalInfoSchema>;
export type CVExperience = z.infer<typeof cvExperienceSchema>;
export type CVEducation = z.infer<typeof cvEducationSchema>;
export type CVSkill = z.infer<typeof cvSkillSchema>;
export type CVLanguage = z.infer<typeof cvLanguageSchema>;
export type GenerateCVInput = z.infer<typeof generateCVSchema>;
export type DownloadCVInput = z.infer<typeof downloadCVSchema>;
export type UpdateCVInput = z.infer<typeof updateCVSchema>;
